import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Input, NativeSelect, cn } from "@ncp/ui";
import type { PageSummary, SpaceText } from "../../pages/api";

/** Moves item i of a list by one place. */
export function move<T>(list: T[], i: number, by: -1 | 1): T[] {
  const j = i + by;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

export function swap<T>(list: T[], a: number, b: number): T[] {
  const next = [...list];
  [next[a], next[b]] = [next[b]!, next[a]!];
  return next;
}

export const replace = <T,>(list: T[], i: number, item: T): T[] => list.map((x, k) => (k === i ? item : x));
export const without = <T,>(list: T[], i: number): T[] => list.filter((_, k) => k !== i);

/** Up, down and remove for one row of an ordered list. */
export function RowActions({ index, count, onMove, onRemove, disabled }: { index: number; count: number; onMove: (by: -1 | 1) => void; onRemove: () => void; disabled?: boolean }) {
  const { t } = useTranslation();
  if (disabled) return null;
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      <Button size="icon-sm" variant="ghost" aria-label={t("admin:spaceBuiltins.moveUp")} disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp /></Button>
      <Button size="icon-sm" variant="ghost" aria-label={t("admin:spaceBuiltins.moveDown")} disabled={index === count - 1} onClick={() => onMove(1)}><ArrowDown /></Button>
      <Button size="icon-sm" variant="ghost" aria-label={t("admin:spaceBuiltins.remove")} onClick={onRemove}><X /></Button>
    </span>
  );
}

/** An English and an Arabic text side by side; empty English clears the pair. */
export function TextPair({ value, onChange, placeholder, readOnly, className }: { value: SpaceText | null | undefined; onChange: (v: SpaceText | null) => void; placeholder?: string; readOnly?: boolean; className?: string }) {
  const { t } = useTranslation();
  const set = (en: string, ar: string) => onChange(en.trim() || ar.trim() ? { en, ar: ar || null } : null);
  return (
    <div className={cn("grid gap-1.5 sm:grid-cols-2", className)}>
      <Input value={value?.en ?? ""} readOnly={readOnly} placeholder={placeholder ?? t("admin:spaceDesigner.textEn")} onChange={(e) => set(e.target.value, value?.ar ?? "")} className="h-8" />
      <Input value={value?.ar ?? ""} readOnly={readOnly} dir="rtl" placeholder={t("admin:spaceDesigner.textAr")} onChange={(e) => set(value?.en ?? "", e.target.value)} className="h-8" />
    </div>
  );
}

/** A page by key, from the pages that support the slot (others stay selectable when already chosen); free text when the list is unavailable. */
export function PagePicker({ value, onChange, pages, slot, readOnly, className }: { value: string | null | undefined; onChange: (key: string) => void; pages: PageSummary[] | null; slot?: string; readOnly?: boolean; className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!pages) return <Input value={value ?? ""} readOnly={readOnly} dir="ltr" className={cn("h-8 font-mono", className)} placeholder={t("admin:spaceDesigner.pageKey")} onChange={(e) => onChange(e.target.value.trim())} />;
  const fit = pages.filter((p) => !slot || p.slots.includes(slot));
  const other = value && !fit.some((p) => p.key === value) ? pages.find((p) => p.key === value) ?? null : null;
  const label = (p: PageSummary) => `${(locale === "ar" && p.nameAr) || p.name} (${p.key})${p.status !== "Published" ? ` · ${t(`admin:pageStudio.status.${p.status}`)}` : ""}`;
  return (
    <NativeSelect value={value ?? ""} disabled={readOnly} onChange={(e) => onChange(e.target.value)} className={cn("h-8", className)}>
      <option value="">{t("admin:spaceDesigner.choosePage")}</option>
      {fit.map((p) => <option key={p.key} value={p.key}>{label(p)}</option>)}
      {other ? <option value={other.key}>{label(other)} · {t("admin:spaceDesigner.otherSlot")}</option> : null}
      {value && !pages.some((p) => p.key === value) ? <option value={value}>{value} · {t("admin:spaceDesigner.missingPage")}</option> : null}
    </NativeSelect>
  );
}

/** Words entered as a comma-separated list. */
export function ListInput({ value, onChange, placeholder, readOnly, className }: { value: string[] | null | undefined; onChange: (v: string[]) => void; placeholder?: string; readOnly?: boolean; className?: string }) {
  return (
    <Input value={(value ?? []).join(", ")} readOnly={readOnly} dir="ltr" placeholder={placeholder} className={cn("h-8 font-mono", className)}
      onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
  );
}

export function SectionTitle({ icon, title, hint, actions }: { icon: ReactNode; title: ReactNode; hint?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-start gap-2">
      <div className="min-w-0 flex-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold">{icon} {title}</h3>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-1.5">{actions}</div> : null}
    </div>
  );
}
